import React, { Suspense } from 'react'
import { Navigate } from 'react-router-dom'
import ProtectedRoute from '../components/ProtectedRoute'
import PermissionRouteGuard from '../components/PermissionRouteGuard.jsx'
import { createPreloadableLazyComponent } from './componentPreloader.js'
import { RouteLoadingComponent } from '../components/SubtleLoadingStates.jsx'
import { isFeatureEnabled } from './featureFlags'

// Lazy load components with preloading support
const Dashboard = createPreloadableLazyComponent(
  () => import('../features/dashboard/components/DailyDashboard'),
  'dashboard'
)
const Clients = createPreloadableLazyComponent(
  () => import('../features/clients/Clients'),
  'clients'
)
const Employees = createPreloadableLazyComponent(
  () => import('../features/employees/Employees'),
  'employees'
)
const Teams = createPreloadableLazyComponent(
  () => import('../features/employees/Teams'),
  'teams'
)
// POST MVP: Communications component - hidden for MVP release
// const Communications = createPreloadableLazyComponent(
//   () => import('../features/communications/Communications'),
//   'communications'
// )
const JobsList = createPreloadableLazyComponent(
  () => import('../features/jobs/JobsList'),
  'jobs'
)
const Estimates = createPreloadableLazyComponent(
  () => import('../features/estimates/Estimates'),
  'estimates'
)
const Services = createPreloadableLazyComponent(
  () => import('../features/services/Services'),
  'services'
)
const Invoices = createPreloadableLazyComponent(
  () => import('../features/invoices/Invoices'),
  'invoices'
)
const Scheduling = createPreloadableLazyComponent(
  () => import('../features/scheduling/Scheduling'),
  'scheduling'
)
const Inspection = createPreloadableLazyComponent(
  () => import('../features/inspection/Inspection'),
  'inspection'
)
const Automations = createPreloadableLazyComponent(
  () => import('../features/automations/AutomationsMain'),
  'automations'
)
// Conditionally load integrations based on feature flag
const Integrations = isFeatureEnabled('INTEGRATIONS_ENABLED') 
  ? createPreloadableLazyComponent(
      () => import('../features/integrations/Integrations'),
      'integrations'
    )
  : null
// Reports consolidated into Finances — kept for legacy redirect only
const CompanyBranding = createPreloadableLazyComponent(
  () => import('../features/settings/CompanyBranding'),
  'company-branding'
)
const SettingsHome = createPreloadableLazyComponent(
  () => import('../features/settings/Settings'),
  'settings'
)
const Account = createPreloadableLazyComponent(
  () => import('../features/settings/Account'),
  'account'
)
const BusinessTaxSettings = createPreloadableLazyComponent(
  () => import('../features/settings/BusinessTaxSettings'),
  'business-tax'
)
const DataImport = createPreloadableLazyComponent(
  () => import('../features/settings/DataImport'),
  'data-import'
)
const Billing = createPreloadableLazyComponent(
  () => import('../features/settings/Billing'),
  'billing'
)
const UpgradeToPaid = createPreloadableLazyComponent(
  () => import('../pages/UpgradeFromTrial'),
  'upgrade-to-paid'
)
const SubmitTicket = createPreloadableLazyComponent(
  () => import('../features/settings/SubmitTicket'),
  'submit-ticket'
)
const NotificationsSettings = createPreloadableLazyComponent(
  () => import('../features/settings/NotificationsSettings'),
  'notifications-settings'
)
const SchedulingSettingsPage = createPreloadableLazyComponent(
  () => import('../features/settings/Scheduling'),
  'scheduling-settings'
)
const SecuritySettings = createPreloadableLazyComponent(
  () => import('../features/settings/Security'),
  'security-settings'
)
const AppearanceSettings = createPreloadableLazyComponent(
  () => import('../features/settings/Appearance'),
  'appearance-settings'
)
const StripeSettings = createPreloadableLazyComponent(
  () => import('../features/settings/integrations/StripeSettings'),
  'stripe-settings'
)
const Finances = createPreloadableLazyComponent(
  () => import('../features/finances/Finances'),
  'finances'
)
const Expenses = createPreloadableLazyComponent(
  () => import('../features/expenses/Expenses'),
  'expenses'
)
const RecurringExpenses = createPreloadableLazyComponent(
  () => import('../features/expenses/RecurringExpenses'),
  'recurring-expenses'
)
const ExpenseCategories = createPreloadableLazyComponent(
  () => import('../features/settings/ExpenseCategories'),
  'expense-categories'
)
// Conditionally load ToolBelt based on feature flag
const ToolBelt = isFeatureEnabled('TOOLBELT_ENABLED') 
  ? createPreloadableLazyComponent(
      () => import('../features/toolbelt/ToolBelt'),
      'toolbelt'
    )
  : null
const TimeOff = createPreloadableLazyComponent(
  () => import('../features/timeoff/TimeOff'),
  'time-off'
)
const ChatPanel = createPreloadableLazyComponent(
  () => import('../features/toolbelt/ai-assistant/ChatPanel'),
  'chat-panel'
)

// Widgets components
const EmbedWidgetManager = createPreloadableLazyComponent(
  () => import('../features/widgets/EmbedWidgetManager'),
  'widget-manager'
)

// Files component
const Files = createPreloadableLazyComponent(
  () => import('../features/files/Files'),
  'files'
)

// Subscription components
const SubscriptionSuccess = createPreloadableLazyComponent(
  () => import('../features/subscriptions/SubscriptionSuccess'),
  'subscription-success'
)


// Enhanced loading component with retry mechanism
const LoadingSpinner = ({ message = "Loading...", onRetry, error = null, routeName = null }) => {
	if (error) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
				<div className="text-center">
					<div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
						<i className="fas fa-exclamation-triangle text-red-500 text-xl"></i>
					</div>
					<p className="text-red-600 mb-4">{error}</p>
					{onRetry && (
						<button 
							onClick={onRetry}
							className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
						>
							Retry
						</button>
					)}
				</div>
			</div>
		)
	}

	// Use subtle loading component for normal loading states
	return <RouteLoadingComponent routeName={routeName || message} />
}

// Enhanced route wrapper with error boundary, retry mechanism, and authentication protection
const RouteWithSuspense = ({ component: Component, fallbackMessage, routeName }) => {
	const [error, setError] = React.useState(null)
	const [retryCount, setRetryCount] = React.useState(0)
	const [isLoading, setIsLoading] = React.useState(true)

	const handleRetry = React.useCallback(() => {
		setError(null)
		setRetryCount(prev => prev + 1)
		setIsLoading(true)
		// Clear any cached modules to force reload
		if (typeof window !== 'undefined' && window.location) {
			window.location.reload()
		}
	}, [])

	const handleError = React.useCallback((error) => {
		console.error('Route loading error:', error)
		// Check if it's a syntax error (likely HTML instead of JS)
		if (error.message && error.message.includes('Unexpected token')) {
			setError('Failed to load component. This may be due to a network issue or cached files. Please try refreshing the page.')
		} else {
			setError(error.message || 'Failed to load component')
		}
		setIsLoading(false)
	}, [])

	const handleLoad = React.useCallback(() => {
		setIsLoading(false)
		setError(null)
	}, [])

	// Reset error when retry count changes
	React.useEffect(() => {
		setError(null)
		setIsLoading(true)
	}, [retryCount])

	// Wrap the component loading in a try-catch for additional error handling
	const SafeComponent = React.useMemo(() => {
		try {
			return React.createElement(Component, {
				key: `${routeName}-${retryCount}`,
				onLoad: handleLoad,
				onError: handleError
			})
		} catch (error) {
			console.error('Error creating component:', error)
			handleError(error)
			return null
		}
	}, [Component, routeName, retryCount, handleLoad, handleError])

	return (
		<Suspense 
			fallback={<LoadingSpinner message={fallbackMessage} routeName={routeName} />}
		>
			<ErrorBoundary onError={handleError}>
				<ProtectedRoute>
					{SafeComponent}
				</ProtectedRoute>
			</ErrorBoundary>
		</Suspense>
	)
}

// Simple error boundary for route components
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props)
		this.state = { hasError: false, error: null }
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, error }
	}

	componentDidCatch(error, errorInfo) {
		console.error('Route component error:', error, errorInfo)
		if (this.props.onError) {
			this.props.onError(error)
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<LoadingSpinner 
					message="Component failed to load"
					error={this.state.error?.message || 'An error occurred'}
					onRetry={() => this.setState({ hasError: false, error: null })}
				/>
			)
		}

		return this.props.children
	}
}

// Main application routes
export const appRoutes = [
{
	path: "/expenses/recurring",
	element: (
		<PermissionRouteGuard requiredTab="expenses" requiredPermission="expenses:manage">
			<RouteWithSuspense component={RecurringExpenses} fallbackMessage="Loading recurring expenses..." routeName="Recurring Expenses" />
		</PermissionRouteGuard>
	),
	title: "Recurring Expenses",
	icon: "fas fa-sync",
	key: "expenses"
},
	{
		path: "/expenses",
		element: (
			<PermissionRouteGuard requiredTab="expenses" requiredPermission="expenses:read">
				<RouteWithSuspense component={Expenses} fallbackMessage="Loading expenses..." routeName="Expenses" />
			</PermissionRouteGuard>
		),
		title: "Expenses",
		icon: "fas fa-receipt",
		key: "expenses"
	},
	{
		path: "/finances",
		element: (
			<PermissionRouteGuard requiredTab="expenses" requiredPermission="expenses:read">
				<RouteWithSuspense component={Finances} fallbackMessage="Loading finances..." routeName="Finances" />
			</PermissionRouteGuard>
		),
		title: "Finances",
		icon: "fas fa-dollar-sign",
		key: "finances"
	},
	{
		path: "/dashboard",
		element: (
			<PermissionRouteGuard requiredTab="dashboard">
				<RouteWithSuspense component={Dashboard} fallbackMessage="Loading dashboard..." routeName="Dashboard" />
			</PermissionRouteGuard>
		),
		title: "Dashboard",
		icon: "fas fa-tachometer-alt",
		key: "dashboard"
	},

	{
		path: "/settings",
		element: (
			<RouteWithSuspense component={SettingsHome} fallbackMessage="Loading settings..." routeName="Settings" />
		),
		title: "Settings",
		icon: "fas fa-cog",
		key: "settings"
	},
  {
    path: "/settings/account",
    element: (
      <RouteWithSuspense component={Account} fallbackMessage="Loading account settings..." routeName="Account Settings" />
    ),
    title: "Account",
    icon: "fas fa-user",
    key: "settings"
  },
  {
    path: "/settings/business-tax",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={BusinessTaxSettings} fallbackMessage="Loading business settings..." routeName="Business & Tax Settings" />
      </PermissionRouteGuard>
    ),
    title: "Business & Tax",
    icon: "fas fa-building",
    key: "settings"
  },
  {
    path: "/settings/billing",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={Billing} fallbackMessage="Loading billing..." routeName="Billing" />
      </PermissionRouteGuard>
    ),
    title: "Billing",
    icon: "fas fa-credit-card",
    key: "settings"
  },
  {
    path: "/settings/subscription",
    element: (
      <RouteWithSuspense component={UpgradeToPaid} fallbackMessage="Loading subscription options..." routeName="Subscription" />
    ),
    title: "Subscription",
    icon: "fas fa-crown",
    key: "settings"
  },
  {
    path: "/settings/submit-ticket",
    element: (
      <RouteWithSuspense component={SubmitTicket} fallbackMessage="Loading ticket submission..." routeName="Submit Ticket" />
    ),
    title: "Submit Ticket",
    icon: "fas fa-comment-dots",
    key: "settings"
  },
  {
    path: "/settings/expense-categories",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={ExpenseCategories} fallbackMessage="Loading expense categories..." routeName="Expense Categories" />
      </PermissionRouteGuard>
    ),
    title: "Expense Categories",
    icon: "fas fa-tags",
    key: "settings"
  },
  {
    path: "/settings/notifications",
    element: (
      <RouteWithSuspense component={NotificationsSettings} fallbackMessage="Loading notifications..." routeName="Notifications" />
    ),
    title: "Notifications",
    icon: "fas fa-bell",
    key: "settings"
  },
  {
    path: "/settings/scheduling",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={SchedulingSettingsPage} fallbackMessage="Loading scheduling..." routeName="Scheduling & Availability" />
      </PermissionRouteGuard>
    ),
    title: "Scheduling & Availability",
    icon: "fas fa-calendar-alt",
    key: "settings"
  },
  {
    path: "/settings/security",
    element: (
      <RouteWithSuspense component={SecuritySettings} fallbackMessage="Loading security settings..." routeName="Security" />
    ),
    title: "Security",
    icon: "fas fa-shield-alt",
    key: "settings"
  },
  {
    path: "/settings/appearance",
    element: (
      <RouteWithSuspense component={AppearanceSettings} fallbackMessage="Loading appearance..." routeName="Appearance" />
    ),
    title: "Appearance",
    icon: "fas fa-sun",
    key: "settings"
  },
  ...(isFeatureEnabled('INTEGRATIONS_ENABLED') && Integrations ? [{
    path: "/settings/integrations/quickbooks",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={Integrations} fallbackMessage="Loading QuickBooks settings..." routeName="QuickBooks" />
      </PermissionRouteGuard>
    ),
    title: "QuickBooks",
    icon: "fas fa-file-invoice-dollar",
    key: "settings"
  }, {
    // Legacy redirect: the standalone /integrations sidebar page and the
    // old QB_APP_RETURN_URL both used /integrations. Redirect to the
    // consolidated Settings page instead of 404-ing bookmarks. The
    // /integrations/quickbooks/callback landing page is mounted in
    // PublicRoutes.jsx and matched before this catch-all.
    path: "/integrations",
    element: <Navigate to="/settings/integrations/quickbooks" replace />,
    title: "QuickBooks (legacy redirect)",
    icon: "fas fa-file-invoice-dollar",
    key: "settings"
  }, {
    path: "/integrations/*",
    element: <Navigate to="/settings/integrations/quickbooks" replace />,
    title: "QuickBooks (legacy redirect)",
    icon: "fas fa-file-invoice-dollar",
    key: "settings"
  }] : []),
  {
    path: "/settings/integrations/stripe",
    element: (
      <PermissionRouteGuard requiredTab="settings">
        <RouteWithSuspense component={StripeSettings} fallbackMessage="Loading Stripe settings..." routeName="Payments & Processing" />
      </PermissionRouteGuard>
    ),
    title: "Payments & Processing",
    icon: "fas fa-money-check-alt",
    key: "settings"
  },
	{
		path: "/settings/data-import",
		element: (
			<PermissionRouteGuard requiredTab="settings">
				<RouteWithSuspense component={DataImport} fallbackMessage="Loading data import..." routeName="Data Import" />
			</PermissionRouteGuard>
		),
		title: "Data Import",
		icon: "fas fa-file-import",
		key: "settings"
	},
	{
		path: "/clients/*",
		element: (
			<PermissionRouteGuard requiredTab="clients">
				<RouteWithSuspense component={Clients} fallbackMessage="Loading clients..." routeName="Clients" />
			</PermissionRouteGuard>
		),
		title: "Clients",
		icon: "fas fa-users",
		key: "clients"
	},
	{
		path: "/employees",
		element: (
			<PermissionRouteGuard requiredTab="employees">
				<RouteWithSuspense component={Employees} fallbackMessage="Loading employees..." routeName="Employees" />
			</PermissionRouteGuard>
		),
		title: "Employees",
		icon: "fas fa-user-tie",
		key: "employees"
	},
	{
		path: "/teams",
		element: (
			<PermissionRouteGuard requiredTab="employees">
				<RouteWithSuspense component={Teams} fallbackMessage="Loading teams..." routeName="Teams" />
			</PermissionRouteGuard>
		),
		title: "Teams",
		icon: "fas fa-users-cog",
		key: "teams"
	},
	{
		path: "/time-off",
		element: (
			<PermissionRouteGuard requiredTab="employees">
				<RouteWithSuspense component={TimeOff} fallbackMessage="Loading time off..." routeName="Time Off" />
			</PermissionRouteGuard>
		),
		title: "Time Off",
		icon: "fas fa-calendar-minus",
		key: "time-off"
	},
	// POST MVP: Communications route - hidden for MVP release
	// {
	// 	path: "/communications",
	// 	element: (
	// 		<PermissionRouteGuard requiredTab="communications">
	// 			<RouteWithSuspense component={Communications} fallbackMessage="Loading communications..." routeName="Communications" />
	// 		</PermissionRouteGuard>
	// 	),
	// 	title: "Communications",
	// 	icon: "fas fa-comments",
	// 	key: "communications"
	// },
	{
		path: "/jobs",
		element: (
			<PermissionRouteGuard requiredTab="jobs">
				<RouteWithSuspense component={JobsList} fallbackMessage="Loading jobs..." routeName="Jobs" />
			</PermissionRouteGuard>
		),
		title: "Jobs",
		icon: "fas fa-briefcase",
		key: "jobs"
	},
	{
		path: "/estimates",
		element: (
			<PermissionRouteGuard requiredTab="estimates">
				<RouteWithSuspense component={Estimates} fallbackMessage="Loading estimates..." routeName="Estimates" />
			</PermissionRouteGuard>
		),
		title: "Estimates",
		icon: "fas fa-calculator",
		key: "estimates"
	},
	{
		path: "/estimate/:id",
		element: (
			<PermissionRouteGuard requiredTab="estimates">
				<RouteWithSuspense component={Estimates} fallbackMessage="Loading estimate..." routeName="Estimate Details" />
			</PermissionRouteGuard>
		),
		title: "Estimate Details",
		icon: "fas fa-calculator",
		key: "estimates"
	},
	{
		path: "/estimate/:id/pdf",
		element: (
			<PermissionRouteGuard requiredTab="estimates">
				<RouteWithSuspense component={Estimates} fallbackMessage="Loading estimate PDF..." routeName="Estimate PDF" />
			</PermissionRouteGuard>
		),
		title: "Estimate PDF",
		icon: "fas fa-file-pdf",
		key: "estimates"
	},
	{
		path: "/services",
		element: (
			<PermissionRouteGuard requiredTab="services">
				<RouteWithSuspense component={Services} fallbackMessage="Loading services..." routeName="Services" />
			</PermissionRouteGuard>
		),
		title: "Services",
		icon: "fas fa-cogs",
		key: "services"
	},
	{
		path: "/invoices",
		element: (
			<PermissionRouteGuard requiredTab="invoices">
				<RouteWithSuspense component={Invoices} fallbackMessage="Loading invoices..." routeName="Invoices" />
			</PermissionRouteGuard>
		),
		title: "Invoices",
		icon: "fas fa-file-invoice-dollar",
		key: "invoices"
	},
	{
		path: "/scheduling",
		element: (
			<PermissionRouteGuard requiredTab="scheduling">
				<RouteWithSuspense component={Scheduling} fallbackMessage="Loading scheduling..." routeName="Scheduling" />
			</PermissionRouteGuard>
		),
		title: "Scheduling",
		icon: "fas fa-calendar-alt",
		key: "scheduling"
	},
	// Conditionally include inspection route based on feature flag (POST-MVP FEATURE)
	...(isFeatureEnabled('INSPECTION_ENABLED') ? [{
		path: "/inspection",
		element: (
			<PermissionRouteGuard requiredTab="inspection">
				<RouteWithSuspense component={Inspection} fallbackMessage="Loading inspection..." routeName="Inspection" />
			</PermissionRouteGuard>
		),
		title: "Inspection",
		icon: "fas fa-clipboard-check",
		key: "inspection"
	}] : []),
	{
		path: "/automations",
		element: (
			<PermissionRouteGuard requiredTab="automations">
				<RouteWithSuspense component={Automations} fallbackMessage="Loading automations..." routeName="Automations" />
			</PermissionRouteGuard>
		),
		title: "Automations",
		icon: "fas fa-robot",
		key: "automations"
	},
	{
		path: "/reports",
		element: (<Navigate to="/finances" replace />),
		title: "Reports (Legacy)",
		icon: "fas fa-chart-bar",
		key: "finances"
	},
	// Widgets route - Professional+ only (check in component)
	{
		path: "/widgets",
		element: (
			<PermissionRouteGuard requiredTab="widgets">
				<RouteWithSuspense component={EmbedWidgetManager} fallbackMessage="Loading widgets..." routeName="Widgets" />
			</PermissionRouteGuard>
		),
		title: "Widgets",
		icon: "fas fa-code",
		key: "widgets"
	},
	{
		path: "/files",
		element: (
			<PermissionRouteGuard requiredTab="files">
				<RouteWithSuspense component={Files} fallbackMessage="Loading files..." routeName="Files" />
			</PermissionRouteGuard>
		),
		title: "Files",
		icon: "fas fa-folder",
		key: "files"
	},
	// Conditionally include toolbelt route based on feature flag
	...(isFeatureEnabled('TOOLBELT_ENABLED') ? [{
		path: "/toolbelt",
		element: (
			<PermissionRouteGuard requiredTab="toolbelt">
				<RouteWithSuspense component={ToolBelt} fallbackMessage="Loading tool belt..." routeName="Tool Belt" />
			</PermissionRouteGuard>
		),
		title: "Tool Belt",
		icon: "fas fa-toolbox",
		key: "toolbelt"
	}] : []),
	{
		path: "/ai",
		element: (
			<PermissionRouteGuard requiredTab="ai-assistant">
				<RouteWithSuspense component={ChatPanel} fallbackMessage="Opening Propseria AI..." routeName="Propseria AI" />
			</PermissionRouteGuard>
		),
		title: "Propseria AI",
		icon: "fas fa-robot",
		key: "ai-assistant"
	},
	{
		path: "/toolbelt/ai",
		element: (<Navigate to="/ai" replace />),
		title: "Propseria AI (Legacy)",
		icon: "fas fa-robot",
		key: "ai-assistant"
	},
	// Legacy calculators route redirected to Tool Belt (only when toolbelt is enabled)
	...(isFeatureEnabled('TOOLBELT_ENABLED') ? [{
		path: "/calculators",
		element: (<Navigate to="/toolbelt" replace />),
		title: "Calculators (Legacy)",
		icon: "fas fa-calculator",
		key: "toolbelt"
	}] : []),
	{
		path: "/settings/branding",
		element: (
			<PermissionRouteGuard requiredTab="settings">
				<RouteWithSuspense component={CompanyBranding} fallbackMessage="Loading company branding..." routeName="Company Branding" />
			</PermissionRouteGuard>
		),
		title: "Company Branding",
		icon: "fas fa-palette",
		key: "settings"
	},
	{
		path: "/company-branding",
		element: (<Navigate to="/settings/branding" replace />),
		title: "Company Branding (Legacy)",
		icon: "fas fa-palette",
		key: "settings"
	},
	// Subscription routes
	{
		path: "/subscription/success",
		element: (
			<RouteWithSuspense component={SubscriptionSuccess} fallbackMessage="Loading success page..." routeName="Subscription Success" />
		),
		title: "Subscription Success",
		icon: "fas fa-check-circle",
		key: "subscription"
	},
 
]

// Helper function to get route by path with error handling
export const getRouteByPath = (path) => {
	try {
		return appRoutes.find(route => {
			if (route.path === path) return true
			if (route.path.endsWith('/*') && path.startsWith(route.path.slice(0, -2))) return true
			return false
		})
	} catch (error) {
		console.error('Error getting route by path:', error)
		return null
	}
}

// Helper function to get active tab from path with error handling
export const getActiveTabFromPath = (path) => {
	try {
		const route = getRouteByPath(path)
		return route ? route.key : 'dashboard'
	} catch (error) {
		console.error('Error getting active tab from path:', error)
		return 'dashboard'
	}
}

// Helper function to get route by key with error handling
export const getRouteByKey = (key) => {
	try {
		// Prefer the primary/base route for this key when multiple routes share the same key
		const candidates = appRoutes.filter(route => route.key === key)
		if (!candidates || candidates.length === 0) {
			return null
		}
		// Prefer exact base path "/${key}" when available (e.g., "/expenses")
		const basePath = `/${key}`
		const baseMatch = candidates.find(route => route.path === basePath)
		if (baseMatch) {
			return baseMatch
		}
		// Otherwise, prefer the shortest path for the key (e.g., "/settings" over "/settings/billing")
		return candidates.reduce((best, current) => {
			if (!best) return current
			return current.path.length < best.path.length ? current : best
		}, null)
	} catch (error) {
		console.error('Error getting route by key:', error)
		return null
	}
} 